import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import api from '../services/api';
import PriceHistoryChart from './PriceHistoryChart';
import Modal from './Modal';

/**
 * Severity buckets for normalized-price changes. Above 10% is red (high
 * urgency), 5-10% is amber (worth monitoring), below 5% never surfaces
 * because the endpoint already filters by threshold (default 5%).
 */
function severityFor(absPct) {
  if (absPct >= 10) return 'high';
  if (absPct >= 5) return 'medium';
  return 'low';
}

/**
 * Phase 11.1 follow-up: Price Alerts section at the top of the Analytics
 * page. Surfaces normalized-price increases (or decreases) since the last
 * known price for the same (vendor, item). Backend filters out pack-size
 * changes that don't actually move $/canonical_unit, so this only flags
 * real cost movement.
 *
 * Default: 5% threshold, 90-day window, increases only. All three are
 * configurable from the section header.
 *
 * @returns {JSX.Element}
 */
function PriceAlerts() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [threshold, setThreshold] = useState(5);
  const [days, setDays] = useState(90);
  const [direction, setDirection] = useState('increase');
  const [historyTarget, setHistoryTarget] = useState(null); // { item_name, sku }

  const loadAlerts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.getPriceChanges({ threshold, days, direction });
      setAlerts(res.data || []);
    } catch (err) {
      console.error('Error loading price alerts:', err);
      setError('Failed to load price alerts');
    } finally {
      setLoading(false);
    }
  }, [threshold, days, direction]);

  useEffect(() => {
    loadAlerts();
  }, [loadAlerts]);

  // Ruth UAT 2026-06-02 (GH #173): she deleted a $0 invoice and noticed
  // the price-history chart updated (CASCADE wiped the price_history row)
  // but Price Alerts still surfaced the now-stale price increase because
  // the component mounts once and never re-fetches. Refetch when the tab
  // regains focus / becomes visible so returning from the Invoices page
  // (where she'd just deleted) refreshes the alerts.
  useEffect(() => {
    const refresh = () => { if (document.visibilityState === 'visible') loadAlerts(); };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [loadAlerts]);

  const directionLabel = direction === 'increase' ? 'increases'
    : direction === 'decrease' ? 'decreases'
    : 'changes';

  return (
    <div className="card price-alerts" role="region" aria-label="Price alerts">
      <div className="price-alerts__header">
        <div>
          <h3 className="settings-form__section-title price-alerts__title">
            Price Alerts
          </h3>
          <p className="price-alerts__subtitle">
            Normalized $/unit {directionLabel} {'>'}{threshold}% in the last {days} days
          </p>
        </div>
        <div className="price-alerts__controls">
          <label className="price-alerts__control">
            <span>Threshold</span>
            <select
              className="input price-alerts__select"
              value={threshold}
              onChange={e => setThreshold(Number(e.target.value))}
              aria-label="Minimum percent change"
            >
              <option value={2}>2%</option>
              <option value={5}>5%</option>
              <option value={10}>10%</option>
              <option value={15}>15%</option>
              <option value={25}>25%</option>
            </select>
          </label>
          <label className="price-alerts__control">
            <span>Window</span>
            <select
              className="input price-alerts__select"
              value={days}
              onChange={e => setDays(Number(e.target.value))}
              aria-label="Time window"
            >
              <option value={30}>30 days</option>
              <option value={60}>60 days</option>
              <option value={90}>90 days</option>
              <option value={180}>180 days</option>
              <option value={365}>1 year</option>
            </select>
          </label>
          <label className="price-alerts__control">
            <span>Direction</span>
            <select
              className="input price-alerts__select"
              value={direction}
              onChange={e => setDirection(e.target.value)}
              aria-label="Direction"
            >
              <option value="increase">Increases only</option>
              <option value="decrease">Decreases only</option>
              <option value="both">Both</option>
            </select>
          </label>
        </div>
      </div>

      {loading && (
        <p role="status" className="price-alerts__status">Loading price alerts…</p>
      )}

      {error && !loading && (
        <p role="alert" className="price-alerts__status price-alerts__status--error">{error}</p>
      )}

      {!loading && !error && alerts.length === 0 && (
        <div className="price-alerts__empty">
          ✓ No notable price {directionLabel} in the last {days} days.
        </div>
      )}

      {!loading && !error && alerts.length > 0 && (
        <div className="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th scope="col">Item</th>
                <th scope="col">SKU</th>
                <th scope="col">Vendor</th>
                <th scope="col" className="text-right">Old $/unit</th>
                <th scope="col" className="text-right">New $/unit</th>
                <th scope="col" className="text-right">Change</th>
                <th scope="col">From</th>
                <th scope="col">To</th>
                <th scope="col"></th>
              </tr>
            </thead>
            <tbody>
              {alerts.map((a, idx) => {
                const sev = severityFor(Math.abs(a.change_pct));
                const sign = a.change_pct > 0 ? '+' : '';
                const unit = a.canonical_unit || 'unit';
                return (
                  <tr key={`${a.sku || a.item_name}-${idx}`} className={`price-alerts__row price-alerts__row--${sev}`}>
                    <td>{a.item_name}</td>
                    <td><span className="td-amt">{a.sku || '—'}</span></td>
                    <td>{a.vendor_name}</td>
                    <td className="text-right">
                      <span className="td-amt">${Number(a.old_normalized_price).toFixed(2)}/{unit}</span>
                    </td>
                    <td className="text-right">
                      <span className="td-amt">${Number(a.new_normalized_price).toFixed(2)}/{unit}</span>
                    </td>
                    <td className="text-right">
                      <span className={`price-alerts__pct price-alerts__pct--${sev}`}>
                        {sign}{a.change_pct}%
                      </span>
                    </td>
                    <td><small>{a.old_date}</small></td>
                    <td><small>{a.new_date}</small></td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => setHistoryTarget({ item_name: a.item_name, sku: a.sku })}
                      >
                        History
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {historyTarget && (
        <Modal
          isOpen={true}
          onClose={() => setHistoryTarget(null)}
          title={`Price History: ${historyTarget.item_name}`}
          size="large"
        >
          <PriceHistoryChart
            itemName={historyTarget.item_name}
            sku={historyTarget.sku}
            onClose={() => setHistoryTarget(null)}
          />
        </Modal>
      )}
    </div>
  );
}

PriceAlerts.propTypes = {};

export default PriceAlerts;
